/* eslint-disable max-lines */
import { uniqBy, prop, sort } from 'ramda';
import { ApolloLink, InMemoryCache, makeVar, Reference } from '@apollo/client/core';
import { ScholarPaperSearchQuery, Code, Project, ProjectQuery, Quotation } from '@/graphql/operations.gen';
import { sortCodeByName } from '@common/codes/sortCodeByName';

export const codeOccurrenceReactive = makeVar<Record<string, number>>({});
export const quotationSourcesReactive = makeVar<Map<string, string>>(new Map());
export const localConfigReactive = makeVar<Record<string, any>>({});
export const aiCodingCreatedCodesReactive = makeVar<Array<Code>>([]);
export const aiCodingCreatedQuotationsReactive = makeVar<Array<Quotation>>([]);

// single fake id for all deleted users
const deletedUserId = '839587b2-8d2f-41a2-adaa-2a46ba19c01d';

export const computeCodeOccurrences = (project: ProjectQuery['project']) => {
  const codes = new Map<string, number>();

  // initialize all code occurrences to 0
  for (const code of project.codes) {
    codes.set(code.id, 0);
  }

  for (const source of project.sources) {
    for (const quotation of source.quotations) {
      for (const codeAttachment of quotation.codes) {
        const codeId = codeAttachment.code.id;

        // increment code occurrence count
        const codeCount = codes.get(codeId);
        codes.set(codeId, codeCount + 1);

        const parentCodeId = codeAttachment.code.parent?.id;

        if (parentCodeId) {
          // increment parent occurrence as well
          const parentCodeCount = codes.get(parentCodeId);
          codes.set(parentCodeId, parentCodeCount + 1);
        }
      }
    }
  }

  codeOccurrenceReactive({ ...Object.fromEntries(codes) });
};

/**
 * This link computes data derived from the project query:
 * - the sources of quotations
 * - the code occurrences within the whole project
 *
 * This is done in a link because we only want to do this when getting fresh
 * data from the server. In vue-apollo, it's not possible to know
 * if the data is fresh or cached
 *
 * The reactive variables are also updated when other requests finish, e.g.
 * the code occurrence is updated when a code is added to a quotation
 */
export const computeLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    if (operation.operationName === 'Project' && response.data?.project) {
      const project: ProjectQuery['project'] = response.data.project;

      // Get source of quotations
      const quotationSourceRefMap = new Map<string, string>();

      project.sources.forEach((source) => {
        source.quotations.forEach((quotation) => {
          quotationSourceRefMap.set(quotation.id, source.id);
        });
      });

      quotationSourcesReactive(quotationSourceRefMap);

      // Compute code occurrences
      computeCodeOccurrences(project);
    }

    return response;
  })
);

/**
 * Returns the in memory cache for apollo-client
 */
export const getCache = () =>
  new InMemoryCache({
    typePolicies: {
      CodeAttachment: {
        // Code attachments don't have their own ids (because we never use them), so the cache id
        // is computed from the quotation id and the code id
        keyFields: ['quotationId', 'code', ['id']],
      },
      Group: {
        fields: {
          contents: {
            merge: false,
          },
        },
      },
      Project: {
        fields: {
          activeTeamMembers: {
            merge: false,
          },
          leftTeamMembers: {
            merge: false,
          },
          sources: {
            merge: false,
          },
          codes: {
            // sort incoming codes by name and replace cache to make sure
            // all places that expect them in alphabetical order (grid tree)
            // work correctly
            merge(_existing, incoming, options) {
              return sort(
                sortCodeByName,
                incoming.map((code) => {
                  const parent = options.readField('parent', code) as Readonly<Reference>;

                  return {
                    ...code,
                    name: options.readField('name', code),
                    parent: parent && { name: options.readField('name', parent) },
                  };
                })
              );
            },
          },
          memos: {
            merge: false,
          },
          reports: {
            merge: false,
          },
          codeGroups: {
            merge: false,
          },
          sourceGroups: {
            merge: false,
          },
          memoGroups: {
            merge: false,
          },
          codeFolders: {
            merge: false,
          },
          sourceFolders: {
            merge: false,
          },
          memoFolders: {
            merge: false,
          },
          quotations: {
            merge: false,
            read(_existing, { readField }) {
              return (readField('sources') as Array<Reference>).flatMap((ref) => readField('quotations', ref));
            },
          },
          recentCodeIds: {
            read(_existing, options): Array<string> {
              const projectId = options.readField('id') as string;

              const recentCodeIds = localConfigReactive().projects?.[projectId]?.recentCodeIds || [];

              return recentCodeIds;
            },
          },
          aiCodingJobs: {
            merge: false,
          },
        },
      },
      ProjectImportUser: {
        keyFields: ['aprxId'],
      },
      ScholarPaperSearchResponse: {
        fields: {
          results: {
            read(existing: ScholarPaperSearchQuery['scholarPaperSearch']['results'], { variables }) {
              // If we read the field before any data has been written to the
              // cache, this function will return undefined, which correctly
              // indicates that the field is missing.

              const max = variables.skip + variables.limit;

              const page = existing && existing.slice(variables.skip, max);

              // If we ask for a page outside the bounds of the existing array,
              // page.length will be 0, and we should return undefined instead of
              // the empty array.
              if (page && page.length > 0) {
                return page;
              }

              return undefined;
            },
            merge(
              existing: ScholarPaperSearchQuery['scholarPaperSearch']['results'],
              incoming: ScholarPaperSearchQuery['scholarPaperSearch']['results'],
              options
            ) {
              const { variables } = options;

              const mergedResults = existing ? existing.slice(0) : [];

              if (variables.skip === 0 && variables.limit === Infinity) {
                // request for all possible items

                if (!existing) {
                  return incoming;
                }

                return uniqBy(prop('paperId'), [...incoming, ...existing]);
              }

              // Insert the incoming elements in the right places, according to variables.
              const end = variables.skip + Math.min(variables.limit, incoming.length);

              for (let index = variables.skip; index < end; ++index) {
                mergedResults[index] = incoming[index - variables.skip];
              }

              return mergedResults;
            },
          },
        },
      },
      Query: {
        fields: {
          activeUsersBySource: {
            merge: false,
          },
          aiCodingCreatedCodes: {
            read() {
              return [...aiCodingCreatedCodesReactive()];
            },
          },
          aiCodingCreatedQuotations: {
            read() {
              return [...aiCodingCreatedQuotationsReactive()];
            },
          },
        },
      },
      Source: {
        fields: {
          codes: {
            merge: false,
            /**
             * Get unique codes in a source
             *
             * The function uses loops as a performance optimization
             */
            read: (_existing = [], options) => {
              const codes = new Map();

              (options.readField('quotations') as Array<Reference>).forEach((quotationRef) => {
                (options.readField('codes', quotationRef) as Array<Reference>).forEach((codeAttachmentRef) => {
                  const code: { id: string } = options.readField('code', codeAttachmentRef as Reference);

                  if (!codes.has(code.id)) {
                    codes.set(code.id, code);
                  }
                });
              });

              return [...codes.values()];
            },
          },
          groups: {
            merge: false,
          },
        },
      },
      Quotation: {
        fields: {
          source: {
            read(_existing, options) {
              const id: string = options.readField('id');

              return options.toReference({ __typename: 'Source', id: quotationSourcesReactive().get(id) });
            },
          },
        },
      },
      Code: {
        fields: {
          occurrence: {
            read(_existing, options) {
              return codeOccurrenceReactive()[options.readField('id') as string] || 0;
            },
          },
        },
      },
      User: {
        fields: {
          id: {
            read(_existing) {
              if (!_existing) {
                return deletedUserId;
              }

              return _existing;
            },
          },
          projects: {
            merge(existing = [], incoming: Array<Project>) {
              return uniqBy(prop('__ref'), [...incoming, ...existing]);
            },
          },
          projectsJoined: {
            merge(existing = [], incoming: Array<Project>) {
              return uniqBy(prop('__ref'), [...incoming, ...existing]);
            },
          },
          projectsInvited: {
            merge: false,
          },
          fullname: {
            read(_existing, options) {
              const firstname = options.readField('firstname');
              const lastname = options.readField('lastname');

              return !firstname && !lastname ? null : `${firstname} ${lastname}`.trim();
            },
          },
          isPlaceholder: {
            read(_existing, options) {
              const firstname = options.readField('firstname');
              const lastname = options.readField('lastname');

              // User is a PLACEHOLDER (i.e. imported without a match in ACO / Cloud) if they only have a firstname
              return firstname && !lastname;
            },
          },
        },
      },
      Subscription: {
        fields: {
          sourcesCreated: {
            merge: false,
          },
        },
      },
    },
  });
